import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * Stripe events viewer. Read-only by default; reprocess is a manual action
 * (clears processed_at + processing_error and surfaces the Stripe Dashboard
 * URL the operator clicks to actually re-deliver).
 *
 * The list endpoint deliberately omits the JSON payload to keep responses
 * small; clicking a row fetches the detail with payload.
 */

interface EventRow {
  id: string;
  type: string;
  tenant_id: number | null;
  tenant_slug: string | null;
  processed_at: string | null;
  processing_error: string | null;
  received_at: string;
}

interface EventDetail extends EventRow {
  payload: unknown;
}

type StatusFilter = 'all' | 'processed' | 'errored' | 'unprocessed';

export function StripeEvents() {
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [type, setType] = useState('');
  const [tenant, setTenant] = useState('');
  const [open, setOpen] = useState<EventDetail | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const qs = new URLSearchParams();
      if (status !== 'all') qs.set('status', status);
      if (type.trim()) qs.set('type', type.trim());
      if (tenant.trim()) qs.set('tenant', tenant.trim());
      qs.set('limit', '100');
      const data = await api<{ events: EventRow[] }>(`/admin/stripe-events?${qs}`);
      setEvents(data.events);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  // Reload on filter change (debounced trivially via React's batching).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function openEvent(id: string) {
    setInfo(null);
    try {
      const data = await api<{ event: EventDetail }>(`/admin/stripe-events/${encodeURIComponent(id)}`);
      setOpen(data.event);
    } catch (e) {
      setInfo(`Could not load event: ${(e as Error).message}`);
    }
  }

  async function reprocess(id: string) {
    if (!confirm(
      `Clear processed_at for ${id}? After this you must click "Resend" for this event in the Stripe Dashboard — we don't replay the handler in-process (would skip signature verification + active-provider lookup).`,
    )) return;
    setBusy(true);
    try {
      const resp = await api<{ ok: true; next_step: string }>(
        `/admin/stripe-events/${encodeURIComponent(id)}/reprocess`,
        { method: 'POST', json: {} },
      );
      setInfo(resp.next_step);
      await load();
    } catch (e) {
      setInfo(`Reprocess failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function rowClass(e: EventRow): string {
    if (e.processing_error) return 'err';
    if (!e.processed_at) return 'warn';
    return 'ok';
  }

  return (
    <div>
      <h1>Stripe events</h1>
      <p className="subtitle">
        Webhook deliveries. Failed or unprocessed rows mean a billing
        notification didn't land — investigate before bothering the customer.
      </p>
      {error && <div className="banner err">{error}</div>}
      {info && <div className="banner ok" onClick={() => setInfo(null)}>{info}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="form-row" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
              <option value="all">All</option>
              <option value="errored">Errored</option>
              <option value="unprocessed">Unprocessed</option>
              <option value="processed">Processed (OK)</option>
            </select>
          </div>
          <div>
            <label>Type contains</label>
            <input
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="e.g. invoice.paid"
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            />
          </div>
          <div>
            <label>Tenant slug</label>
            <input
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              placeholder="acme"
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            />
          </div>
          <div className="spacer" />
          <button onClick={() => load()} disabled={busy}>↻ Refresh</button>
        </div>
      </div>

      {!events ? (
        <div className="muted">Loading…</div>
      ) : events.length === 0 ? (
        <div className="card muted">No events match the current filters.</div>
      ) : (
        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '8px 12px' }}>Received</th>
                <th style={{ padding: '8px 12px' }}>Type</th>
                <th style={{ padding: '8px 12px' }}>Tenant</th>
                <th style={{ padding: '8px 12px' }}>Status</th>
                <th style={{ padding: '8px 12px' }}>Event ID</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr
                  key={e.id}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onClick={() => openEvent(e.id)}
                >
                  <td className="muted small" style={{ padding: '8px 12px' }}>
                    {new Date(e.received_at).toLocaleString()}
                  </td>
                  <td className="code" style={{ padding: '8px 12px' }}>{e.type}</td>
                  <td style={{ padding: '8px 12px' }}>{e.tenant_slug ?? '—'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span className={`pill ${rowClass(e)}`}>
                      {e.processing_error ? 'errored' : e.processed_at ? 'ok' : 'unprocessed'}
                    </span>
                    {e.processing_error && (
                      <span className="muted small" title={e.processing_error} style={{ marginLeft: 8 }}>
                        {e.processing_error.slice(0, 60)}{e.processing_error.length > 60 ? '…' : ''}
                      </span>
                    )}
                  </td>
                  <td className="code small muted" style={{ padding: '8px 12px' }}>{e.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50,
          }}
          onClick={() => setOpen(null)}
        >
          <div
            className="card"
            style={{ maxWidth: 800, width: '100%', maxHeight: '85vh', overflow: 'auto', padding: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="button-row" style={{ marginTop: 0, justifyContent: 'space-between' }}>
              <div>
                <div className="code small">{open.id}</div>
                <h3 style={{ marginTop: 4 }}>{open.type}</h3>
              </div>
              <button onClick={() => setOpen(null)}>Close</button>
            </div>
            <table style={{ width: '100%', fontSize: 13, marginTop: 12 }}>
              <tbody>
                <tr><td className="muted" style={{ padding: '4px 8px', width: 160 }}>Received</td><td style={{ padding: '4px 8px' }}>{new Date(open.received_at).toLocaleString()}</td></tr>
                <tr><td className="muted" style={{ padding: '4px 8px' }}>Tenant</td><td style={{ padding: '4px 8px' }}>{open.tenant_slug ?? '—'}</td></tr>
                <tr><td className="muted" style={{ padding: '4px 8px' }}>Processed at</td><td style={{ padding: '4px 8px' }}>{open.processed_at ? new Date(open.processed_at).toLocaleString() : '—'}</td></tr>
                {open.processing_error && (
                  <tr><td className="muted" style={{ padding: '4px 8px' }}>Error</td>
                    <td style={{ padding: '4px 8px', color: 'var(--err)' }}>
                      <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{open.processing_error}</pre>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {(open.processing_error || !open.processed_at) && (
              <div className="button-row" style={{ marginTop: 12 }}>
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={() => reprocess(open.id)}
                  title="Clears local processed_at + error. You then click Resend in the Stripe Dashboard to actually re-deliver."
                >
                  ↻ Mark unprocessed (then resend from Stripe)
                </button>
              </div>
            )}

            <h4 style={{ marginTop: 16 }}>Payload</h4>
            <pre
              style={{
                background: 'var(--soft)', padding: 12, borderRadius: 6,
                fontSize: 12, overflow: 'auto', maxHeight: 400,
              }}
            >
              {JSON.stringify(open.payload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
